<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import {
    applyRound,
    bitCount,
    coordsInsideZone,
    displayPrice,
    getDisplayOptions,
    round2,
    roundPrice,
  } from '../../utils';
  import ItemsService from '../../api/services/items.service';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import { IBranch } from '../../interfaces/IBranch';
  import { Globals } from '../../modules/Globals';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { IRestaurantSettings } from '../../api/services/restaurants.service';
  import ItemOption from '../widgets/ItemOption.vue';
  import AddItemWidget from '../widgets/AddItemWidget.vue';
  import AddItemModal from './new-order/AddItemModal.vue';
  import { IOrderItem } from '../../interfaces/IOrderItem';
  import DisplayValue from '../DisplayValue.vue';
  import { OrderType } from '../../enums/OrderEnums';
  import TelephoneInput, { InputMode } from '../widgets/TelephoneInput.vue';
  import AddressInput from '../widgets/AddressInput.vue';
  import { IDeliveryZone } from '../../interfaces/IDeliveryZone';
  import { ITable } from '../../interfaces/ITable';
  import { IAddress } from '../../interfaces/IAddress';
  import { TaxDisplayMode } from '../../enums/TaxDisplayMode';
  import CustomFieldsService from '../../api/services/custom-fields.service';
  import ICustomField from '../../interfaces/ICustomField';
  import DeliveryZonesService from '../../api/services/delivery-zones.service';
  import TablesService from '../../api/services/tables.service';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import OrdersService, {
    ICreateOrderData,
  } from '../../api/services/orders.service';
  import { PaymentMethod } from '../../enums/PaymentMethod';
  import OrderCreatedModal from './new-order/OrderCreatedModal.vue';
  import { IOrder } from '../../interfaces/IOrder';
  import TwoColumnScrollableModal from './base/TwoColumnScrollableModal.vue';
  import AddItemsModal from './new-order/AddItemsModal.vue';
  import NewOrderItemsContainer from './new-order/partials/NewOrderItemsContainer.vue';
  import NewOrderAddedItems from './new-order/partials/NewOrderAddedItems.vue';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  interface IMenuType {
    menuId: number;
    orderTypes: OrderType[];
    title: string;
  }

  @Component({
    components: {
      TwoColumnScrollableModal,
      AddressInput,
      TelephoneInput,
      DisplayValue,
      AddItemModal,
      AddItemWidget,
      ItemOption,
      NewOrderItemsContainer,
      NewOrderAddedItems,
    },
  })
  export default class NewOrderModal extends Vue {
    $refs!: {
      telInput: any;
    };
    @Prop() restaurant: IRestaurant;
    @Prop() branch: IBranch;
    @Prop() currency: ICurrency;
    @Prop() restaurantSettings: IRestaurantSettings;
    @Prop() editingOrder: IOrder;
    @Prop() editingOrderItems: IOrderItem[];
    customFieldsService: CustomFieldsService = new CustomFieldsService();
    ordersService = new OrdersService();
    tablesService = new TablesService();
    deliveryZonesService = new DeliveryZonesService();
    itemsService = new ItemsService();
    displayPrice = displayPrice;
    paymentMethods = [];
    countries = Globals.countries;
    restaurantStore = useRestaurantStore();

    uploadsPath = Globals.uploadsPath;
    valid: boolean = false;
    loading: boolean = false;
    orderItems: IOrderItem[] = [];
    orderData = {
      type: OrderType.Seat,
      tel: null,
      whatsapp: null,
      name: null,
      comment: null,
    };
    deliveryZones: IDeliveryZone[] = [];

    tables: ITable[] = [];

    customFields: ICustomField[] = null;
    customFieldValues: { [id: number]: string } = {};

    currentAddress: IAddress = null;
    table: ITable = null;
    totals = {
      totalPrice: 0,
      totalCount: 0,
      taxAmount: 0,
      subTotal: 0,
    };

    selectedMenuType: IMenuType = null;

    creating = false;

    async created() {
      this.paymentMethods = [
        { id: PaymentMethod.Cash, title: this.$t('paymentMethods.cash') },
        { id: PaymentMethod.Card, title: this.$t('paymentMethods.card') },
        //{ id: PaymentMethod.Online, title: 'MercadoPago' },
        //{ id: PaymentMethod.RedSys, title: 'Tarjeta / Bizum' },
      ];

      await Promise.all([
        this.loadDeliveryZones(),
        this.loadTables(),
        this.updateOrderTypes(),
      ]);

      if (this.editingOrder) {
        await this.initEditingOrder();
      }
    }

    mounted() {
      this.refreshTotals();
    }

    async initEditingOrder() {
      this.orderData.type = this.editingOrder.type;
      this.orderData.name = this.editingOrder.userName;
      this.orderData.tel = this.editingOrder.userTel;
      this.orderData.whatsapp = this.editingOrder.userWhatsapp;
      this.orderData.comment = this.editingOrder.comment;

      await this.$nextTick();

      this.table = this.editingOrder.table;

      if (this.editingOrder.type === OrderType.Delivery) {
        this.currentAddress = {
          address: this.editingOrder.userAddress,
          address2: this.editingOrder.userAddress2,
          comments: this.editingOrder.userAddressComments,
          lat: this.editingOrder.deliveryLat,
          lng: this.editingOrder.deliveryLng,
        };
      }

      if (this.editingOrder.orderFields) {
        for (const field of this.editingOrder.orderFields) {
          this.customFieldValues[field.fieldId] = field.value;
        }
      }

      const orderItems = await this.ordersService.getOrderItems(
        this.editingOrder.id
      );

      this.orderItems = orderItems
        .filter((oI) => !oI.canceledAt)
        .map((i) => {
          return {
            ...i,
          };
        });

      this.refreshTotals();
    }

    async loadTables() {
      this.tables = await TablesService.getAll(
        this.restaurant.id,
        this.branch.id
      );
    }

    async loadDeliveryZones() {
      this.deliveryZones = await DeliveryZonesService.getByBranch(
        this.branch.id
      );
    }

    setPhoneNumber(data: { type: InputMode; number: string }) {
      switch (data.type) {
        case InputMode.Telephone:
          this.orderData.tel = data.number;
          this.orderData.whatsapp = null;
          break;
        case InputMode.WhatsApp:
          this.orderData.whatsapp = data.number;
          this.orderData.tel = null;
          break;
      }
    }

    /* async openAddItemDialog() {
      this.$modal.show(
        AddItemsModal,
        {
          restaurant: this.restaurant,
          branch: this.branch,
          currency: this.currency,
          restaurantSettings: this.restaurantSettings,
          menuId: this.selectedMenuType?.menuId ?? null,
          totals: this.totals,
        },
        {
          name: 'add-items',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: window.innerHeight * 0.95,
          width: 960,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          add: (orderItem: IOrderItem) => {
            this.orderItems.push(orderItem);
            this.refreshTotals();
          },
        }
      );
    } */

    onItemAdded(orderItem: IOrderItem) {
      // If an item with the same id and options is already in the list, just increase the amount
      const existing = this.orderItems.find(
        (i) =>
          i.itemId === orderItem.itemId &&
          !orderItem.options.length &&
          !i.options.length &&
          (i.comment || '') === (orderItem.comment || '')
      );

      if (existing) {
        const unitPrice = existing.total / existing.amount;
        existing.amount += orderItem.amount;
        existing.total = roundPrice(unitPrice * existing.amount, true);
      } else {
        this.orderItems.push(orderItem);
      }

      this.refreshTotals();

      vueData.addAlert(
        `${orderItem.item.name} agregado`,
        AlertType.Success,
        1000
      );
    }

    async edit(orderItem: IOrderItem) {
      this.$modal.show(
        AddItemModal,
        {
          edit: orderItem,
          restaurant: this.restaurant,
          branch: this.branch,
          currency: this.currency,
          restaurantSettings: this.restaurantSettings,
        },
        {
          name: 'add-item',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          //width: 1000,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          add: (newOI: IOrderItem) => {
            const index = this.orderItems.indexOf(orderItem);
            Vue.set(this.orderItems, index, newOI);
            this.refreshTotals();
          },
        }
      );
    }

    remove(orderItem: IOrderItem) {
      const index = this.orderItems.indexOf(orderItem);
      this.orderItems.splice(index, 1);
      this.refreshTotals();
    }

    async cancel() {
      this.$emit('close');
    }

    async save() {
      try {
        const order = await this.makeOrder();
        if (order) {
          vueData.noNotifyOrderIds.push(order.id);
          this.$parent.$emit('created', order);

          //if (order.type === OrderType.Away) this.showCreatedModal(order);

          this.$emit('close');
        }
      } catch (e) {
        console.error(e);
        vueData.serverError(e);
      }
    }

    showCreatedModal(order: IOrder) {
      this.$modal.show(
        OrderCreatedModal,
        {
          order,
        },
        {
          name: 'order-created',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          width: 380,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        }
      );
    }

    // ORDER RELATED

    get menuTypes(): IMenuType[] {
      const orderTypesByMenuId: { [menuId: number]: OrderType[] } = {};

      const orderTypes = [];
      if (this.tableOrderAvailable) orderTypes.push(OrderType.Seat);
      if (this.takeAwayAvailable) orderTypes.push(OrderType.Away);
      if (this.deliveryAvailable) orderTypes.push(OrderType.Delivery);

      const orderTypeNames = {
        [OrderType.Seat]: this.$t('orderTypes.seatShort'),
        [OrderType.Away]: this.$t('orderTypes.takeAwayShort'),
        [OrderType.Delivery]: this.$t('orderTypes.deliveryShort'),
      };

      for (let i = 1; i <= 3; i++) {
        if (!orderTypes[i - 1]) continue;

        const menuId = this.restaurantSettings[`menu${i}Id`];
        if (!orderTypesByMenuId[menuId]) orderTypesByMenuId[menuId] = [];
        orderTypesByMenuId[menuId].push(orderTypes[i - 1]);
      }

      const types = [];
      const keys = Object.keys(orderTypesByMenuId);

      if (keys.length === 1)
        types.push({
          menuId: keys[0] !== 'null' ? keys[0] : null,
          orderTypes: [...orderTypes.filter((v) => v !== null)],
          name: 'Menú por defecto',
        });
      else {
        for (const menuId of keys) {
          const menuOrderTypes = orderTypesByMenuId[menuId];
          const titles = menuOrderTypes.map((type) => orderTypeNames[type]);
          const title = this.$t('menuTitle', {
            menus: `${titles.join(' y ')}`,
          });
          types.push({
            menuId: menuId !== 'null' ? menuId : null,
            orderTypes: menuOrderTypes,
            name: title,
          });
        }
      }

      //types.push({ orderType: OrderType.Seat, name: 'Menú de salón' });

      return types;
    }

    addressChanged(data) {
      this.currentAddress = {
        address: data.address,
        address2: data.address2,
        comments: data.comments,
        lat: data.lat,
        lng: data.lng,
      };
    }

    get shipsToCurrentAddress() {
      if (this.orderData.type !== OrderType.Delivery) return true;

      if (!this.currentAddress?.address) return false;

      if (!this.branch.requireFullAddress) return true;
      else if (this.currentAddress.lat === null) return false;

      if (!this.branch.zonedDelivery) return true;

      return !!this.currentDeliveryZone;
    }

    get activeCustomFields() {
      if (!this.customFields) return [];

      const orderTypes = {
        [OrderType.Seat]: 's',
        [OrderType.Away]: 't',
        [OrderType.Delivery]: 'd',
      };

      const type = orderTypes[this.orderData.type];

      return this.customFields.filter((c) => c.orderTypes.indexOf(type) > -1);
    }

    async makeOrder() {
      if (
        this.orderData.type === OrderType.Away ||
        this.orderData.type === OrderType.Delivery
      ) {
        if (!this.orderData.name || this.orderData.name.trim() === '') {
          vueData.addAlert('Ingrese nombre', AlertType.Error);
          return;
        }
        if (!this.orderData.tel && !this.orderData.whatsapp) {
          vueData.addAlert('Ingrese teléfono', AlertType.Error);
          return;
        }
        if (this.orderData.type === OrderType.Delivery) {
          //if (this.branch.requireFullAddress) {
          if (!this.currentAddress?.address) {
            vueData.addAlert('Ingrese dirección', AlertType.Error);
            return;
          } else if (!this.shipsToCurrentAddress) {
            vueData.addAlert(
              'El restaurant no envía a dicha dirección',
              AlertType.Error
            );
            return;
          }
          /*} else {
          if (!this.userAddress || this.userAddress.trim() === '') {
            Toast.fail(this.$t('orderMessages.enterAddress'));
            return;
          }
        }*/
        }
      }

      if (this.activeCustomFields.length) {
        for (const field of this.activeCustomFields.filter(
          (c) => c.mandatory
        )) {
          if (!this.customFieldValues[field.id]) {
            vueData.addAlert(
              this.$tc('Complete los campos obligatorios'),
              AlertType.Error
            );
            return;
          }
        }
      }

      this.loading = true;
      this.creating = true;

      try {
        const items = [];
        for (const item of this.orderItems) {
          const i = { ...item };
          // @ts-ignore
          i.options = i.options.map((option) => {
            return { id: option.optionItemId, val: option.val };
          });
          items.push({
            itemId: i.itemId,
            comment: i.comment,
            options: i.options,
            amount: i.amount,
          });
        }
        const orderData: ICreateOrderData = <ICreateOrderData>{
          restaurantId: this.restaurant.id,
          branchId: this.branch.id,
          comment: this.orderData.comment,
          type: this.orderData.type,
          paymentType: 'store',
          paymentMethod: PaymentMethod.Cash,
          items: items.map((i) => {
            const item = { ...i };
            delete item['item'];
            return item;
          }),
          mode: this.branch.ordersMode,
          manual: true,
          customFields: this.customFieldValues,
          deviceId: this.restaurantStore.deviceId,
        };

        if (
          this.orderData.type === OrderType.Away ||
          this.orderData.type === OrderType.Delivery
        ) {
          orderData.userName = this.orderData.name;
          if (this.orderData.type === OrderType.Delivery) {
            orderData.userAddress = this.currentAddress.address;
            orderData.userAddress2 = this.currentAddress.address2;
            orderData.userAddressComments = this.currentAddress.comments;
            if (this.currentAddress.lat) {
              orderData.deliveryLat = this.currentAddress.lat;
              orderData.deliveryLng = this.currentAddress.lng;
            }
          }
          if (this.orderData.whatsapp)
            orderData.userWhatsapp = this.orderData.whatsapp;
          else orderData.userTel = this.orderData.tel;
        } else {
          orderData.tableId = this.table?.id;
        }

        const order = !this.editingOrder
          ? await this.ordersService.create(orderData)
          : await this.ordersService.update(this.editingOrder.id, orderData);

        if (!this.editingOrder)
          vueData.addAlert(
            'Pedido agregado a Pedidos pendientes',
            AlertType.Success
          );
        else {
          vueData.addAlert('Pedido actualizado', AlertType.Success);
          this.$emit('close');
        }

        return order;
      } catch (e) {
        throw e;
      } finally {
        this.loading = false;
        this.creating = false;
      }
    }

    get telCodes() {
      return this.countries.map((c) => {
        return {
          country: c.value,
          code: c.telCode,
          shortValue: `+${c.telCode}`,
          value: `${c.text} (+${c.telCode})`,
        };
      });
    }

    get activePaymentMethods() {
      switch (this.orderData.type) {
        case OrderType.Seat:
          return this.branch.paymentMethodsA;
        case OrderType.Away:
          return this.branch.paymentMethodsB;
        case OrderType.Delivery:
          return this.branch.paymentMethodsC;
      }
    }

    async updateOrderTypes() {
      if (this.tableOrderAvailable) this.orderData.type = OrderType.Seat;
      else if (this.takeAwayAvailable) this.orderData.type = OrderType.Away;
      else if (this.deliveryAvailable) this.orderData.type = OrderType.Delivery;
      else {
        vueData.addAlert(
          'Debe habilitar algún tipo de pedido',
          AlertType.Error
        );
        this.$emit('close');
      }

      // this.onActivePaymentMethodsChanged(this.activePaymentMethods);

      this.loading = true;
      this.customFields = await CustomFieldsService.getAll(
        this.restaurant.id,
        this.branch.id
      );
      this.customFieldValues = {};
      this.loading = false;
    }

    /* @Watch('activePaymentMethods')
    onActivePaymentMethodsChanged(activeMethods: number) {
      function firstSetBit(number) {
        return number & -number;
      }

      this.paymentMethod = firstSetBit(activeMethods);
    } */

    get currentDeliveryZone() {
      if (
        this.orderData.type !== OrderType.Delivery ||
        !this.branch.zonedDelivery ||
        !this.branch.requireFullAddress ||
        !this.currentAddress ||
        this.currentAddress.lat === null
      )
        return null;

      return coordsInsideZone(
        [this.currentAddress.lat, this.currentAddress.lng],
        this.deliveryZones
      );
    }

    get deliveryFee() {
      if (this.branch.deliveryCost === null) {
        if (this.branch.zonedFee && this.currentDeliveryZone) {
          return (<IDeliveryZone>this.currentDeliveryZone).cost;
        } else {
          return null;
        }
      } else {
        return this.branch.deliveryCost;
      }
    }

    @Watch('orderData.type', { immediate: true })
    onOrderTypeChanged(newType, oldType) {
      this.refreshTotals();

      if (newType !== oldType) {
        this.customFieldValues = {};

        if (this.restaurantSettings.multipleMenus) {
          this.orderItems = [];
          this.selectedMenuType = this.menuTypes.find(
            (t) => t.orderTypes.indexOf(this.orderData.type) > -1
          );
        }
      }
    }

    get tableOrderAvailable() {
      return this.branch.allowOrders && this.branch.tableOrders;
    }

    get takeAwayAvailable() {
      return this.branch.allowOrders && this.branch.takeAway;
    }

    get deliveryAvailable() {
      return this.branch.allowOrders && this.branch.delivery;
    }

    @Watch('deliveryFee')
    deliveryFeeChanged() {
      this.refreshTotals();
    }

    private refreshTotals() {
      const { taxDelivery, taxMode } = this.restaurantSettings;

      let deliveryCost =
        this.orderData.type === OrderType.Delivery && this.deliveryFee !== null
          ? this.deliveryFee
          : 0;

      this.totals.totalPrice = this.totals.totalCount = 0;

      for (let item of this.orderItems) {
        this.totals.totalCount += item.amount;
        this.totals.totalPrice += item.total;
      }

      this.totals.subTotal = this.totals.totalPrice;

      if (taxDelivery && deliveryCost) {
        if (this.restaurantSettings.taxMode !== 3)
          deliveryCost = this.applyTax(deliveryCost);

        this.totals.totalPrice += deliveryCost;
      }

      this.totals.subTotal = this.totals.totalPrice = this.applyRound(
        this.totals.totalPrice
      );

      if (taxMode === TaxDisplayMode.AddAtInvoice && this.restaurant.tax) {
        this.totals.taxAmount = this.applyRound(
          round2(this.totals.totalPrice * (this.restaurant.tax / 100))
        );
        this.totals.totalPrice += this.totals.taxAmount;
      }

      if (!taxDelivery) this.totals.totalPrice += deliveryCost;

      this.totals.totalPrice = this.applyRound(this.totals.totalPrice);
    }

    applyTax(val: number) {
      if (
        this.hasTax &&
        (this.restaurantSettings.taxMode === TaxDisplayMode.AddAndShow ||
          this.restaurantSettings.taxMode === TaxDisplayMode.SilentAdd)
      ) {
        val *= 1 + this.restaurant.tax / 100;
      }
      return val;
    }

    get hasTax() {
      return (
        this.restaurantSettings.taxMode !== TaxDisplayMode.NoTax &&
        this.restaurant.tax
      );
    }

    get multiplePaymentMethods() {
      return bitCount(this.activePaymentMethods) > 1;
    }

    private applyRound(totalPrice: number) {
      return applyRound(
        totalPrice,
        this.restaurantSettings.roundMode,
        this.restaurantSettings.roundInterval
      );
    }
  }
</script>

<template>
  <two-column-scrollable-modal
    :left-width="'4'"
    :right-width="'8'"
    class="new-order-modal"
  >
    <template #title>
      <h3>Nuevo pedido</h3>
    </template>
    <template #left>
      <v-form @submit.prevent v-model="valid">
        <h3>Información del pedido</h3>
        <div class="py-2">
          <h4 class="font-weight-regular">Tipo de pedido</h4>
          <div class="d-flex flex-row justify-space-between align-center py-1">
            <v-btn-toggle
              class="v-btn-toggle--full-width"
              v-model="orderData.type"
              borderless
              dense
              mandatory
              color="primary"
            >
              <v-btn
                value="seat"
                small
                :disabled="!tableOrderAvailable || !!editingOrder"
              >
                <span>A la mesa</span>
              </v-btn>
              <v-btn
                value="away"
                small
                :disabled="!takeAwayAvailable || !!editingOrder"
              >
                <span>Take away</span>
              </v-btn>
              <v-btn
                value="delivery"
                small
                :disabled="!deliveryAvailable || !!editingOrder"
              >
                <span>A domicilio</span>
              </v-btn>
            </v-btn-toggle>
          </div>
          <v-row v-if="orderData.type === 'seat'">
            <v-col cols="12" sm="6" class="pb-0">
              <v-autocomplete
                label="Seleccione la mesa"
                v-model="table"
                :items="tables"
                item-text="name"
                auto-select-first
                return-object
                outlined
                dense
                hide-details
                :disabled="!!editingOrder"
                class="mt-0"
              />
            </v-col>
          </v-row>
        </div>
        <div
          class="py-2"
          v-if="orderData.type !== 'seat' || activeCustomFields.length"
        >
          <h4 class="font-weight-regular">Informaciòn del cliente</h4>
          <div class="">
            <template v-if="orderData.type !== 'seat'">
              <v-row class="mb-n3">
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Nombre"
                    v-model="orderData.name"
                    outlined
                    dense
                    hide-details
                    class="mt-0"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <telephone-input
                    :value="orderData.tel || orderData.whatsapp"
                    :mode-value="orderData.whatsapp ? 1 : 0"
                    :default-country="restaurant.country"
                    @change="setPhoneNumber"
                    :required="false"
                  />
                </v-col>
              </v-row>
              <v-row v-if="orderData.type === 'delivery'">
                <v-col cols="12" class="">
                  <address-input
                    :detailed="branch.requireFullAddress"
                    :branch="branch"
                    :value="currentAddress"
                    @change="addressChanged"
                  />
                </v-col>
              </v-row>
            </template>
            <v-row v-if="activeCustomFields.length">
              <v-col
                cols="6"
                v-for="field of activeCustomFields"
                :key="field.id"
              >
                <v-text-field
                  :label="field.title"
                  v-model="customFieldValues[field.id]"
                  :rules="field.mandatory ? $rule('required') : undefined"
                  outlined
                  dense
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </div>
        </div>
        <h4 class="font-weight-regular">Comentarios del pedido</h4>
        <v-textarea
          class="mt-2"
          placeholder="Comentarios opcionales sobre el pedido"
          outlined
          dense
          hide-details
          :rows="2"
          background-color="#ffffff"
          v-model="orderData.comment"
        />
      </v-form>
      <NewOrderAddedItems
        class="mt-4"
        :order-items="orderItems"
        :totals="totals"
        :order-data="orderData"
        :delivery-fee="deliveryFee"
        @edit="edit"
        @remove="remove"
      />
    </template>
    <template #right>
      <NewOrderItemsContainer
        :menu-id="selectedMenuType ? selectedMenuType.menuId : null"
        @add="onItemAdded"
      />
    </template>
    <template #action-bar>
      <div class="d-flex flex-row align-center justify-end">
        <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
        <v-btn
          color="primary"
          @click="save"
          :disabled="loading || !valid || !orderItems.length"
          >{{ !editingOrder ? $t('create') : $t('save') }}</v-btn
        >
      </div>
    </template>
  </two-column-scrollable-modal>
</template>

<style lang="scss" scoped></style>
